import React from "react";
import { CompanyDocument } from "winslow-core";
import { AppDocumentsUploadButton } from "../../app/app-documents/AppDocumentsUploadButton";
import { AppChanges } from "../../app/app-changes/AppChanges";
import { X } from "lucide-react";
import "./DocumentDrawer.css";

type UploadInfo = {
  name: string;
  description: string;
  requiresSignatures: boolean;
};

type DocumentDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  info: UploadInfo;
  setInfo: (info: UploadInfo) => void;
  selectedFile: File | null;
  onFileSelect: (file: File) => void;
  onSubmit: () => void;
  onCancel: () => void;
  uploading: boolean;
  allowSave: boolean;
  editDocumentId?: string | null;
};

export const DocumentDrawer: React.FC<DocumentDrawerProps> = ({
  isOpen,
  onClose,
  mode,
  info,
  setInfo,
  selectedFile,
  onFileSelect,
  onSubmit,
  onCancel,
  uploading,
  allowSave,
  editDocumentId,
}) => {
  if (!isOpen) return null;

  const isEditMode = mode === "edit";
  const title = isEditMode ? "Edit Document" : "New Document";
  const submitText = uploading 
    ? (isEditMode ? "Saving..." : "Uploading...") 
    : (isEditMode ? "Save Changes" : "Add Document");

  return (
    <div className={`document-drawer-overlay ${isOpen ? "open" : ""}`} onClick={onClose}>
      <div className="document-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="document-drawer-header">
          <h2 className="document-drawer-title">{title}</h2>
          <button className="document-drawer-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <div className="document-drawer-content">
          <div className="document-drawer-form-fields">
            <label className="document-drawer-label">Title</label>
            <input
              type="text"
              className="document-drawer-input"
              value={info.name}
              placeholder="Document Name"
              onChange={(e) => setInfo({ ...info, name: e.target.value })}
            />
            
            <label className="document-drawer-label">Description</label>
            <textarea
              className="document-drawer-textarea"
              value={info.description}
              placeholder="Description"
              rows={3}
              onChange={(e) => {
                const textarea = e.target as HTMLTextAreaElement;
                textarea.style.height = "auto";
                textarea.style.height = textarea.scrollHeight + "px";
                setInfo({ ...info, description: textarea.value });
              }}
            />
            
            {!isEditMode && (
              <div>
                <label className="document-drawer-label">File</label>
                <AppDocumentsUploadButton
                  label={selectedFile ? "Change File" : "Select File"}
                  onFileSelect={onFileSelect}
                  disabled={uploading}
                />
                {selectedFile && (
                  <div className="document-drawer-file-info">
                    <div className="document-drawer-file-name">{selectedFile.name}</div>
                    <div className="document-drawer-file-size">
                      {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="document-drawer-actions">
            <AppChanges
              submitText={submitText}
              saveDisabled={!allowSave}
              onSubmit={onSubmit}
              onCancel={onCancel}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
